import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useEffect, useMemo, useState } from 'react'

type Step = {
    id: string
    label: string
}

type Props = {
    isOpen: boolean
    onClose: () => void
    reportTitle: string
    reportIcon: string
    title?: string
    steps?: Step[]
    durationMs?: number
    description?: string
}

type RenderedStep = Step & {
    isDone: boolean
    isActive: boolean
}

const defaultSteps: Step[] = [
    { id: 'gather', label: 'Gathering data' },
    { id: 'analyse', label: 'Analysing' },
    { id: 'format', label: 'Formatting' },
]

const StepStatus = ({
    isDone,
    isActive,
}: {
    isDone: boolean
    isActive: boolean
}) => {
    if (isDone) {
        return (
            <div className='flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100'>
                <div className='flex h-6 w-6 items-center justify-center rounded-full border-2 border-emerald-500 text-emerald-500'>
                    <svg className='h-4 w-4' fill='none' stroke='currentColor' strokeWidth='2.5' viewBox='0 0 24 24'>
                        <path d='M5 13l4 4L19 7' strokeLinecap='round' strokeLinejoin='round' />
                    </svg>
                </div>
            </div>
        )
    }

    if (isActive) {
        return (
            <div className='flex h-9 w-9 items-center justify-center rounded-full bg-[#004b16]'>
                <div className='h-3.5 w-3.5 rounded-full bg-white animate-pulse' />
            </div>
        )
    }

    return <div className='h-9 w-9 rounded-full border border-slate-300 dark:border-slate-700' />
}

const ReportPreparingModal = ({
    isOpen,
    onClose,
    reportTitle,
    reportIcon,
    title = 'Preparing Your Report',
    steps = defaultSteps,
    durationMs = 4000,
    description,
}: Props) => {
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        if (!isOpen) {
            setActiveIndex(0)
            return
        }

        const safeSteps = steps.length > 0 ? steps : defaultSteps
        const stepDuration = Math.max(700, Math.floor(durationMs / safeSteps.length))
        const timers: number[] = []

        safeSteps.forEach((_, index) => {
            const timer = window.setTimeout(() => {
                setActiveIndex(index)
            }, stepDuration * index)

            timers.push(timer)
        })

        return () => {
            timers.forEach((timer) => window.clearTimeout(timer))
        }
    }, [isOpen, steps, durationMs])

    const renderedSteps = useMemo<RenderedStep[]>(() => {
        const safeSteps = steps.length > 0 ? steps : defaultSteps

        return safeSteps.map((step, index) => ({
            ...step,
            isDone: index < activeIndex,
            isActive: index === activeIndex,
        }))
    }, [steps, activeIndex])

    return (
        <Transition appear as={Fragment} show={isOpen}>
            <Dialog as='div' className='relative z-[125]' onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-200'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-150'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <div className='fixed inset-0 bg-black/40 backdrop-blur-[2px]' />
                </Transition.Child>

                <div className='fixed inset-0 overflow-y-auto'>
                    <div className='flex min-h-full items-center justify-center p-4'>
                        <Transition.Child
                            as={Fragment}
                            enter='ease-out duration-200'
                            enterFrom='opacity-0 translate-y-2 scale-[0.98]'
                            enterTo='opacity-100 translate-y-0 scale-100'
                            leave='ease-in duration-150'
                            leaveFrom='opacity-100 translate-y-0 scale-100'
                            leaveTo='opacity-0 translate-y-2 scale-[0.98]'
                        >
                            <Dialog.Panel className='w-full max-w-4xl overflow-hidden rounded-[32px] border border-slate-200 bg-[#f7f7f7] shadow-2xl dark:border-slate-800 dark:bg-[#09090b]'>
                                <div className='flex items-center justify-between border-b border-slate-200 px-8 py-6 dark:border-slate-800'>
                                    <div className='flex items-center gap-4'>
                                        <img src={reportIcon} alt={`${reportTitle} icon`} className='h-7 w-7 object-contain' />
                                        <Dialog.Title className='text-xl font-semibold text-slate-900 dark:text-slate-50'>
                                            {reportTitle}
                                        </Dialog.Title>
                                    </div>

                                    <button
                                        type='button'
                                        aria-label='Close report preparation'
                                        onClick={onClose}
                                        className='inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
                                    >
                                        <svg className='h-5 w-5' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
                                            <path d='M6 6l12 12M18 6L6 18' strokeLinecap='round' strokeLinejoin='round' />
                                        </svg>
                                    </button>
                                </div>

                                <div className='flex min-h-[500px] flex-col items-center justify-center px-8 py-10'>
                                    <div className='mb-8 flex h-24 w-24 items-center justify-center'>
                                        <img src='/assets/chat/hotelworld-logo.svg' alt='Hotel World AI' className='h-20 object-contain dark:brightness-0 dark:invert' />
                                    </div>

                                    <div className='mb-10 text-center text-xl font-semibold text-slate-900 dark:text-slate-50'>
                                        {title}
                                    </div>

                                    {description && <p role='status' className='mb-8 max-w-lg text-center text-sm text-slate-500 dark:text-slate-400'>{description}</p>}

                                    <div className='w-full max-w-md space-y-6' aria-busy='true'>
                                        {renderedSteps.map((step) => (
                                            <div key={step.id} className='flex items-center gap-4'>
                                                <StepStatus isDone={step.isDone} isActive={step.isActive} />
                                                <span
                                                    className={[
                                                        'text-base font-medium',
                                                        step.isDone || step.isActive
                                                            ? 'text-slate-900 dark:text-slate-50'
                                                            : 'text-slate-400 dark:text-slate-500',
                                                    ].join(' ')}
                                                >
                                                    {step.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default ReportPreparingModal;
